import type { ColonyCollapseResult, Game, Member } from '../../types/game'

interface ColonyCollapseEventOverviewModalProps {
  game: Game
  members: Member[]
  onClose: () => void
}

function formatOffered(offered: Record<string, number>): string {
  const entries = Object.entries(offered).filter(([, n]) => n > 0)
  if (entries.length === 0) return 'nothing'
  return entries.map(([r, n]) => `${n} ${r[0].toUpperCase()}${r.slice(1)}`).join(', ')
}

function PlayerRow({
  result,
  player,
  alias,
}: {
  result: ColonyCollapseResult
  player: Game['players'][number]
  alias: string
}) {
  const offered = result.offered_by_player[player.color] ?? {}

  return (
    <div className="season-overview-slot">
      <div className="season-overview-member-header">
        <div className="opponent-identity">
          <div className="opponent-marker" style={{ background: `var(--${player.color})` }} />
          <div className="opponent-id">{alias}</div>
        </div>
        <ul className="season-overview-player-stats">
          <li>Offered: {formatOffered(offered)}</li>
        </ul>
      </div>
    </div>
  )
}

export function ColonyCollapseEventOverviewModal({
  game,
  members,
  onClose,
}: ColonyCollapseEventOverviewModalProps) {
  const result = game.colony_collapse_result
  if (!result) return null

  const aliasByColor = Object.fromEntries(
    members.filter((m) => m.color).map((m) => [m.color as string, m.alias])
  )

  const title = result.succeeded
    ? `Colony Survived: ${result.event_name}`
    : `Colony Failed: ${result.event_name}`

  const favorLines = result.succeeded
    ? Object.entries(result.favor_by_player)
        .filter(([, n]) => n > 0)
        .map(([color, n]) => `${aliasByColor[color] ?? color} received ${n} Favor`)
    : []

  const failureLines = result.succeeded
    ? []
    : [
        result.n_infections > 0 ? `${result.n_infections} infection${result.n_infections !== 1 ? 's' : ''} crept into the Colony.` : null,
        result.queen_lost_health ? 'The Queen lost 1 Health.' : null,
        result.effect ?? null,
      ].filter(Boolean) as string[]

  return (
    <div className="season-overview-modal-root">
      <div className="season-overview-overlay" onClick={onClose} />
      <div className="season-overview tableau">
        <div className="season-overview-content desktop-shell-view">
          <div className="season-overview-header">{title}</div>
          <div className="season-overview-players">
            {game.players.map((player) => (
              <PlayerRow
                key={player.color}
                result={result}
                player={player}
                alias={aliasByColor[player.color] ?? player.color}
              />
            ))}
          </div>
          {favorLines.length > 0 && (
            <div className="season-overview-slot">
              <ul className="season-overview-player-stats">
                {favorLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}
          {failureLines.length > 0 && (
            <div className="season-overview-slot">
              <ul className="season-overview-player-stats">
                {failureLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="season-overview-close-container">
            <div className="season-overview-close" onClick={onClose}>
              Close
            </div>
          </div>
        </div>
        <div className="season-overview-mobile-shell mobile-shell-view">
          <div className="mobile-modal-shell season-overview-mobile-card">
            <div className="mobile-modal-header">
              <div className="mobile-modal-title">{title}</div>
              <div className="mobile-modal-close-button lobby-start" onClick={onClose}>
                Close
              </div>
            </div>
            <div className="mobile-modal-section">
              <div className="mobile-modal-section-title">Offerings</div>
              <div className="season-overview-mobile-players">
                {game.players.map((player) => {
                  const offered = result.offered_by_player[player.color] ?? {}
                  return (
                    <div key={`mobile-${player.color}`} className="season-overview-mobile-slot">
                      <div className="season-overview-mobile-slot-header">
                        <div className="opponent-marker" style={{ background: `var(--${player.color})` }} />
                        <div className="season-overview-mobile-slot-name">
                          {aliasByColor[player.color] ?? player.color}
                        </div>
                      </div>
                      <ul className="season-overview-mobile-slot-stats">
                        <li>Offered: {formatOffered(offered)}</li>
                      </ul>
                    </div>
                  )
                })}
              </div>
            </div>
            {favorLines.length > 0 && (
              <div className="mobile-modal-section">
                <div className="mobile-modal-section-title">Favor</div>
                <div className="season-overview-mobile-slot">
                  <ul className="season-overview-mobile-slot-stats">
                    {favorLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {failureLines.length > 0 && (
              <div className="mobile-modal-section">
                <div className="mobile-modal-section-title">Consequence</div>
                <div className="season-overview-mobile-slot">
                  <ul className="season-overview-mobile-slot-stats">
                    {failureLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
